"use client";

import { useEffect, useState } from "react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  if (loading) {
    return <div className="hidden md:flex gap-1">
        <div className="h-9 w-16 bg-secondary-light animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-secondary-light animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-secondary-light animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-secondary-light animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-secondary-light animate-pulse rounded-md" />
      </div>;
  }
  return <>
      {}
      <NavigationMenu viewport={false} className="hidden md:flex">
        <NavigationMenuList>
          {menuItems.map(item => <NavigationMenuItem key={item.id}>
              <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="relative inline-flex h-9 items-center justify-center px-4 py-2 text-sm font-medium text-foreground-light transition-all hover:!bg-transparent focus:!bg-transparent group">
                  {item.label}
                  <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-primary transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] group-hover:w-full" />
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>)}
        </NavigationMenuList>
      </NavigationMenu>

      {}
      <Button variant="ghost" size="icon" className="md:hidden hover:!bg-transparent focus:!bg-transparent" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
        {mobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
      </Button>

      {}
      {mobileMenuOpen && <div className="absolute top-full left-0 w-full bg-light-background border-b border-border shadow-medium md:hidden">
          <nav className="container mx-auto px-4 py-4">
            <ul className="flex flex-col gap-2">
              {menuItems.map(item => <li key={item.id}>
                  <Link to={item.href || "#"} className="block px-4 py-2 text-sm font-medium text-foreground-light hover:bg-secondary-light rounded-md transition-all hover:!bg-secondary-light focus:!bg-secondary-light" onClick={() => setMobileMenuOpen(false)}>
                    {item.label}
                  </Link>
                </li>)}
            </ul>
          </nav>
        </div>}
    </>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header className={`sticky top-0 z-50 w-full bg-light-background border-b border-border transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${scrolled ? "h-16 shadow-medium" : "h-22"}`}>
      <div className="container mx-auto px-4 h-full">
        <div className="flex items-center justify-between h-full gap-8">
          <Link to="/#" className="flex-shrink-0">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1497613/img/coastal_ai_technology_logo.png" alt="Coastal AI & Technology LLC" className="h-7" />
          </Link>

          <div className="flex-1 flex justify-center">
            <MenuProvider menu_id="17">
              <HeaderMenuConsumer />
            </MenuProvider>
          </div>

          <Button asChild className={`hidden md:inline-flex hover:!bg-primary-hover focus:!bg-primary-hover transition-all duration-[80ms] ease-[var(--ease-spring)] ${scrolled ? "hover:-translate-y-0.5 hover:shadow-medium" : ""}`}>
            <Link to="/#contact" className="items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive bg-primary shadow-xs h-9 px-4 py-2 has-[>svg]:px-3 hidden transition-all duration-[80ms] ease-[var(--ease-spring)] !text-[hsl(210,20%,98%)] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-primary/90 md:inline-flex hover:!bg-primary-hover focus:!bg-primary-hover hover:-translate-y-0.5 hover:shadow-medium">Get Started</Link>
          </Button>
        </div>
      </div>
    </header>;
}